import { useState } from 'react';
import {
  Box,
  Button,
  Link,
  Icon,
  Radio,
  Sidebar,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import { ChevronLeftIcon, ExclamationCircleIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { WhatsappCreditsPacksResponseDto } from '@tiendanube/common';
import Country from '@tiendanube/common/src/enums/Country';
import { useNavegate } from 'App/hooks';
import { useToast } from 'commons/hooks';
import { formatCurrency } from 'commons/utils';
import { useAdminLinks, useGetCountry } from 'domains/Auth/hooks';
import { useCreateWhatsappPurchaseMutation } from 'domains/Configurations/Notifications/notificationsApi';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';

const termsAndConditionsUrls = {
  ptBR: 'https://www.nuvemshop.com.br/termos-de-uso/nuvemshop',
  esAR: 'https://www.tiendanube.com/terminos-de-uso/tiendanube',
};

interface WhatsAppBannerPurchaseSidebarProps {
  isOpen: boolean;
  onClick: () => void;
  onRemove: () => void;
  creditsPacks: WhatsappCreditsPacksResponseDto | undefined;
}

export function WhatsAppBannerPurchaseSidebar({
  isOpen,
  onClick,
  onRemove,
  creditsPacks,
}: Readonly<WhatsAppBannerPurchaseSidebarProps>) {
  const t = useTranslationConfigurations('notifications.whatsapp');
  const {
    i18n: { language },
  } = useTranslation();
  const { goTo } = useNavegate();
  const { getNewAdminLink } = useAdminLinks();
  const country = useGetCountry();
  const { addToast } = useToast();
  const [selectedPackIndex, setSelectedPackIndex] = useState<number>(0);

  const [createWhatsappPurchase, { isLoading }] =
    useCreateWhatsappPurchaseMutation();

  const handleCreateWhatsappPurchase = async () => {
    const credits = creditsPacks?.packs?.[selectedPackIndex]?.credits ?? 0;

    try {
      const result = await createWhatsappPurchase({ credits }).unwrap();

      const callbackUrl = `${getNewAdminLink(
        'settings/notifications',
      )}?purchaseId=${result.id}`;

      goTo(
        `/account/checkout/choose-payment-method/pay-order/${
          result.pay_order_id
        }?callbackUrl=${encodeURIComponent(callbackUrl)}`,
      );
    } catch (error) {
      addToast({
        appearance: 'danger',
        label: t('purchase.errorMessage'),
      });
    }
  };

  return (
    <Sidebar
      padding="base"
      open={isOpen}
      onRemove={() => {
        setSelectedPackIndex(0);
        onRemove();
      }}
    >
      <Box marginBottom="2">
        <Button
          appearance="transparent"
          onClick={() => {
            setSelectedPackIndex(0);
            onClick();
          }}
          aria-label={t('purchase.back')}
        >
          <Icon
            color="primary-textLow"
            source={<ChevronLeftIcon size="small" />}
          />
        </Button>
      </Box>

      <Sidebar.Header title={t('purchase.title')}>
        <Box paddingY="2">
          <Text fontSize="caption" color="neutral-textLow">
            {t('purchase.description')}
          </Text>
        </Box>
      </Sidebar.Header>

      <Sidebar.Body>
        <Box
          width="100%"
          height="auto"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          gap="1"
        >
          {creditsPacks?.packs?.map(({ price, credits }, index) => {
            const lastItem = index === (creditsPacks?.packs?.length ?? 0) - 1;

            return (
              <Box
                key={`pack-${index}`}
                width="100%"
                height="auto"
                borderStyle="solid"
                borderColor="neutral-surfaceHighlight"
                borderWidth="none"
                borderTopWidth="1"
                borderBottomWidth={lastItem ? '1' : 'none'}
                paddingTop="3"
                paddingBottom="1-5"
                gap="2"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap="1"
                >
                  <Radio
                    id={`pack-${index}`}
                    name="whatsapp-pack"
                    label={`${credits} ${t('banner.creditsUnit')}`}
                    value={index}
                    checked={selectedPackIndex === index}
                    onChange={(e) =>
                      setSelectedPackIndex(Number(e.target.value))
                    }
                  />
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  paddingLeft="5"
                >
                  <Text fontSize="caption">
                    {formatCurrency(price, creditsPacks.currency, language)}
                  </Text>
                </Box>
              </Box>
            );
          })}
          <Box
            width="100%"
            height="auto"
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            justifyContent="flex-end"
            marginTop="2"
          >
            <Button
              appearance="primary"
              onClick={handleCreateWhatsappPurchase}
              disabled={isLoading || Number.isNaN(selectedPackIndex)}
            >
              {isLoading ? <Spinner size="small" /> : t('purchase.payButton')}
            </Button>
          </Box>
        </Box>
      </Sidebar.Body>

      <Sidebar.Footer>
        <Box
          width="100%"
          height="auto"
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          justifyContent="flex-start"
          gap="2"
        >
          <Icon
            color="primary-textLow"
            source={<ExclamationCircleIcon size="small" />}
          />
          <Text fontSize="caption" color="neutral-textLow" as="span">
            {t('banner.expirationText')}{' '}
            <Link
              as="a"
              href={
                country === Country.BR
                  ? termsAndConditionsUrls.ptBR
                  : termsAndConditionsUrls.esAR
              }
              target="_blank"
              rel="noopener noreferrer"
              appearance="primary"
              fontSize="caption"
              textDecoration="underline"
            >
              {t('banner.termsAndConditions')}
            </Link>
          </Text>
        </Box>
      </Sidebar.Footer>
    </Sidebar>
  );
}
